import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import { Checkbox } from './checkbox';
import { cn } from '../../lib/utils';

// Personal Data Categories with items
export const PERSONAL_DATA_CATEGORIES = [
  {
    category: 'Biographical / Identity',
    items: ['First and Last Name', 'Date of Birth', 'Gender', 'Marital Status', 'Nationality', 'Country of Residence']
  },
  {
    category: 'Contact Information',
    items: ['Home Address', 'Mailing Address', 'Telephone Number (Home)', 'Mobile Phone Number', 'Email Address (Personal)', 'Emergency Contact Details']
  },
  {
    category: 'Physical Characteristics',
    items: ['Height', 'Weight', 'Age', 'Hair Colour', 'Eye Colour', 'Skin Tone', 'Distinguishing Marks']
  },
  {
    category: 'Family Information',
    items: ['Spouse Name', 'Spouse Identification', 'Dependents Names', 'Dependents Ages', 'Next of Kin Details', 'Beneficiary Information', 'Guardian / Legal Representative', 'Power of Attorney Holder']
  },
  {
    category: 'Identification Documents',
    items: ['National ID Card Number', 'Passport Number', "Driver's License Number", 'Tax Identification Number (TRN/TIN)', 'Social Security / NIS Number', 'Professional License Numbers', 'Membership Numbers', 'Employee ID Number']
  },
  {
    category: 'Employment / Professional',
    items: ['Place of Employment', 'Job Title', 'Employment History', 'Salary', 'Work Performance Records', 'Disciplinary Records', 'Training Records', 'Professional Certifications', 'Work Permit / Visa Status', 'Reference Details']
  },
  {
    category: 'Educational',
    items: ['Educational Qualifications', 'Schools / Institutions Attended', 'Student ID Number', 'Professional Training Completed']
  },
  {
    category: 'Financial',
    items: ['Bank Account Number', 'Credit Card Details', 'Investment Information', 'Loans / Mortgage Information', 'Income Level / Salary Range', 'Assets and Liabilities', 'Policy Number (Insurance)', 'Proof of Address (Utility Bill)']
  },
  {
    category: 'Credit Information',
    items: ['Credit Records', 'Credit Scores', 'Credit Reports', 'Credit History']
  },
  {
    category: 'Transactional',
    items: ['Purchase History', 'Transaction Records', 'Payment History', 'Order Details', 'Subscription Information', 'Insurance Claims History']
  },
  {
    category: 'Digital / Online',
    items: ['IP Address', 'Cookies and Tracking Data', 'Login Credentials (Username)', 'Social Media Handles / Profiles', 'Geolocation Data (GPS coordinates)']
  },
  {
    category: 'Communication',
    items: ['Email Content', 'Call Recordings', 'SMS / Text Messages', 'Chat / Messaging Logs', 'Video Conference Recordings', 'Voicemail Recordings', 'Correspondence Records']
  },
  {
    category: 'Visual / Media',
    items: ['Photographs', 'Video Recordings (CCTV, interviews)', 'Audio Recordings', 'Signature (handwritten / electronic)', 'Profile Pictures']
  },
  {
    category: 'Behavioural / Preferences',
    items: ['Marketing Preferences', 'Communication Preferences', 'Product / Service Preferences', 'Lifestyle Information', 'Hobbies and Interests', 'Dietary Preferences / Restrictions', 'Travel Preferences']
  },
  {
    category: 'Vehicle / Property',
    items: ['Vehicle Registration Number', "Driver's History", 'Ownership Records', 'Vehicle Insurance Details']
  },
  {
    category: 'Immigration / Travel',
    items: ['Visa Information', 'Work Permit Details', 'Immigration Status', 'Travel History', 'Country of Birth', 'Citizenship Status']
  },
  {
    category: 'Legal / Contractual',
    items: ['Power of Attorney', 'Legal Disputes / Litigation Records', 'Complaints Records']
  },
  {
    category: 'Medical Administration (Non-Clinical)',
    items: ['Scheduling of Medical Appointments', 'Invoices for Healthcare Services', 'Medical Certificates for Sick Leave']
  }
];

// Sensitive Data Categories with items
export const SENSITIVE_DATA_CATEGORIES = [
  {
    category: 'Health / Medical Data',
    items: ['Medical Ailments (Hypertension, Diabetes, STIs, etc.)', 'Medical Referrals', 'Prescriptions', 'Medical Examination Reports', 'Laboratory Tests', 'Disability Information', 'Radiographs, X-Rays, MRIs', 'Mental Health Records', 'Blood Type', 'Allergies', 'Vaccination Records', 'Hospital Admission Records', 'Treatment Plans', 'Prognosis Information']
  },
  {
    category: 'Biometric Data',
    items: ['Facial Recognition Data', 'Fingerprints', 'Voice Recognition Patterns', 'Iris Scanning Data', 'Palmprint Verification Data', 'Retina Recognition Data', 'Gait Analysis', 'Keystroke Dynamics', 'Vein Pattern Recognition']
  },
  {
    category: 'Genetic Data',
    items: ['Chromosomal Analysis', 'DNA Analysis', 'RNA Analysis', 'Genetic Predisposition Information', 'Hereditary Information']
  },
  {
    category: 'Racial / Ethnic Origin',
    items: ['Race', 'National or Ethnic Origin', 'Tribal Affiliation', 'Languages Spoken (where indicative of ethnicity)']
  },
  {
    category: 'Religious / Philosophical Beliefs',
    items: ['Religious Belief', 'Philosophical Belief', 'Place of Worship', 'Religious Practices', 'Spiritual Affiliation']
  },
  {
    category: 'Political Opinions',
    items: ['Political Party Membership', 'Political Views / Opinions', 'Voting Preferences', 'Campaign Contributions', 'Political Activism']
  },
  {
    category: 'Trade Union Membership',
    items: ['Trade Union Membership', 'Union Representative Status', 'Collective Bargaining Participation']
  },
  {
    category: 'Sexual Orientation / Life',
    items: ['Sexual Orientation', 'Gender Identity', 'Sexual History', 'Sexual Preferences']
  },
  {
    category: 'Criminal Records',
    items: ['Arrest Records', 'Conviction Records', 'Pardons', 'Police Certificate of Character', 'Pending Criminal Charges', 'Probation / Parole Status', 'Rehabilitation Records']
  },
  {
    category: 'Psychometric Data',
    items: ['Psychometric Evaluations', 'Personality Assessments', 'IQ Test Results', 'Psychological Profiles']
  },
  {
    category: "Children's Data",
    items: ['Data Relating to Minors (under 18)', 'Parental Consent Records', 'School Records of Minors', 'Child Protection Information']
  }
];

// Accordion Category Selector Component
export const CategoryAccordionSelector = ({ 
  categories, 
  selectedItems, 
  onChange,
  className 
}) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (categoryName) => {
    setExpandedCategory(expandedCategory === categoryName ? null : categoryName);
  };

  const toggleItem = (item) => {
    if (selectedItems.includes(item)) {
      onChange(selectedItems.filter(i => i !== item));
    } else {
      onChange([...selectedItems, item]);
    }
  };

  const getCategorySelectionCount = (category) => {
    return category.items.filter(item => selectedItems.includes(item)).length;
  };

  const isCategoryPartiallySelected = (category) => {
    const count = getCategorySelectionCount(category);
    return count > 0 && count < category.items.length;
  };

  const isCategoryFullySelected = (category) => {
    return category.items.every(item => selectedItems.includes(item));
  };

  const toggleAllInCategory = (category) => {
    const allSelected = isCategoryFullySelected(category);
    if (allSelected) {
      // Deselect all items in this category
      onChange(selectedItems.filter(item => !category.items.includes(item)));
    } else {
      // Select all items in this category
      const newItems = [...selectedItems];
      category.items.forEach(item => {
        if (!newItems.includes(item)) {
          newItems.push(item);
        }
      });
      onChange(newItems);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {categories.map((category) => {
        const isExpanded = expandedCategory === category.category;
        const selectionCount = getCategorySelectionCount(category);
        const hasSelections = selectionCount > 0;
        const isFullySelected = isCategoryFullySelected(category);

        return (
          <div 
            key={category.category}
            className={cn(
              "border rounded-lg overflow-hidden transition-all",
              hasSelections ? "border-primary bg-primary/5" : "border-border",
              isExpanded ? "ring-1 ring-primary" : ""
            )}
          >
            {/* Category Header */}
            <div
              className={cn(
                "flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors",
                hasSelections ? "bg-primary/5" : ""
              )}
              onClick={() => toggleCategory(category.category)}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <span className={cn(
                  "font-medium text-sm",
                  hasSelections ? "text-primary" : ""
                )}>
                  {category.category}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasSelections && (
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full",
                    isFullySelected 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-primary/20 text-primary"
                  )}>
                    {selectionCount}/{category.items.length}
                  </span>
                )}
                {isFullySelected && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
            </div>

            {/* Category Items */}
            {isExpanded && (
              <div className="border-t bg-background">
                {/* Select All option */}
                <div 
                  className="flex items-center gap-2 p-2 px-4 border-b bg-muted/30 cursor-pointer hover:bg-muted/50"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAllInCategory(category);
                  }}
                >
                  <Checkbox 
                    checked={isFullySelected}
                    className="data-[state=checked]:bg-primary"
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    Select All ({category.items.length} items)
                  </span>
                </div>
                
                {/* Individual Items */}
                <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[250px] overflow-y-auto">
                  {category.items.map((item) => (
                    <div
                      key={item}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                        selectedItems.includes(item) ? "bg-primary/10" : ""
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(item);
                      }}
                    >
                      <Checkbox 
                        checked={selectedItems.includes(item)}
                        className="data-[state=checked]:bg-primary"
                      />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryAccordionSelector;
